import { useMutation } from '@apollo/client'
import { APIStatus } from '@flowwai-frontend/shared/api-common'
import { NETWORK_ONLY_FETCH_POLICY } from '@flowwai-frontend/bps-officer-web/graphql'

import CustomError from '../../models/CustomError'
import { convertToErrorType, getAPIStatus } from '../../utils/APIUtils'

import {
   RevokeGoogleUserAuthorizationResultMutation as Mutation,
   RevokeGoogleUserAuthorizationResultDocument as MutationDocument,
   RevokeGoogleUserAuthorizationResultMutationVariables as MutationVariables
} from './mutation.generatedTypes'
import useResponseHandler from './responseHandler'

export interface FailureArgsType {
   data?: CustomError<Mutation['revokeGoogleAuthorization']>
   error?: Error | null
}

interface APIArgs {
   onSuccess: () => void
   onFailure?: (args: FailureArgsType) => void
}

interface ReturnType {
   apiError: Error | null
   apiStatus: APIStatus

   triggerAPI: (args: APIArgs) => void
}

const useRevokeUserGoogleAuthorization = (): ReturnType => {
   const [revokeUserGoogleAuthorization, { error, loading }] = useMutation<
      Mutation,
      MutationVariables
   >(MutationDocument)

   const { handleAPIResponse } = useResponseHandler()

   const triggerAPI = async (args: APIArgs): Promise<void> => {
      const response = await revokeUserGoogleAuthorization({
         fetchPolicy: NETWORK_ONLY_FETCH_POLICY,

         onError(error) {
            args.onFailure?.({ error })
         }
      })
      if (!response.data) return

      handleAPIResponse({
         data: response.data,

         onHandleError: (
            errorMessage: string,
            data: Mutation['revokeGoogleAuthorization']
         ) => args.onFailure?.({ data: new CustomError(errorMessage, data) }),
         onSuccess: args.onSuccess
      })
   }

   const apiError = convertToErrorType(error)
   const apiStatus = getAPIStatus({
      isError: Boolean(error),
      isLoading: loading
   })

   return {
      apiError,
      apiStatus,

      triggerAPI
   }
}

export default useRevokeUserGoogleAuthorization
